/**
 * Leaf editors. Each takes a view node and a setter. They're deliberately dumb
 * — no store access — so they're trivially reusable and testable.
 */

import type { BooleanView, EnumView, NumberView, StringView } from '@bliksys/schema-engine';
import type { JsonValue } from '@bliksys/shared';
import { useState } from 'react';

import { useAssetClient } from '../asset-origin-context.js';

import { AssetGallery } from './AssetGallery.js';

import './LeafEditors.css';

interface StringProps {
  view: StringView;
  onChange: (v: string) => void;
  id: string;
  /**
   * When `view.refsFrom` is set, the parent passes the resolved
   * autocomplete suggestions here. Each option is the FULL ref string
   * (e.g. `#/$defs/MyType`). The input renders them via a `<datalist>`,
   * so free-text entry is preserved — the user can type a ref that
   * doesn't exist yet, which is the right UX when they're about to
   * add a definition for it.
   */
  refOptions?: readonly string[] | undefined;
}

export function StringInput({ view, onChange, id, refOptions }: StringProps): JSX.Element {
  const common = {
    id,
    value: view.value,
    placeholder: view.placeholder,
    minLength: view.minLength,
    maxLength: view.maxLength,
    'aria-required': view.required,
  } as const;

  if (view.widget === 'textarea' || view.widget === 'markdown' || view.widget === 'code') {
    return (
      <textarea
        {...common}
        className={'input input-textarea' + (view.widget === 'code' ? ' input-mono' : '')}
        rows={view.widget === 'markdown' ? 8 : 4}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (view.widget === 'color') {
    return (
      <div className="input-color-row">
        <input
          {...common}
          id={`${id}-swatch`}
          type="color"
          className="input-color-swatch"
          aria-label="Color picker"
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          {...common}
          type="text"
          className="input"
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  if (view.widget === 'image') {
    return <ImageInput view={view} common={common} onChange={onChange} />;
  }

  // Default plain text input. When refOptions are provided, attach a
  // <datalist> for autocomplete suggestions while keeping free-text
  // entry — datalist is a typeahead, not a select, so the user can
  // type a ref that doesn't exist yet (typical when they're about to
  // add the definition for it).
  const datalistId = refOptions && refOptions.length > 0 ? `${id}-options` : undefined;
  return (
    <>
      <input
        {...common}
        type={view.format === 'email' ? 'email' : view.format === 'uri' ? 'url' : 'text'}
        className="input"
        pattern={view.pattern}
        title={view.pattern ? `Must match pattern: ${view.pattern}` : undefined}
        list={datalistId}
        onChange={(e) => onChange(e.target.value)}
      />
      {datalistId && refOptions && (
        <datalist id={datalistId}>
          {refOptions.map((opt) => (
            <option key={opt} value={opt} />
          ))}
        </datalist>
      )}
    </>
  );
}

/**
 * Image-widget input. URL field plus a preview thumbnail. When the
 * project has an asset origin configured, also surfaces a Browse
 * button that opens the gallery picker.
 *
 * The gallery returns either the resolved public URL or just the
 * object key, controlled per-field by `x-bliksys.assetFormat`. Plain
 * URL entry (paste-a-link) is always available even when an origin
 * is configured — the widget never gates writes behind the gallery.
 */
function ImageInput({
  view,
  common,
  onChange,
}: {
  view: StringView;
  common: Record<string, unknown>;
  onChange: (v: string) => void;
}): JSX.Element {
  const client = useAssetClient();
  const [galleryOpen, setGalleryOpen] = useState(false);
  return (
    <div className="input-image-row">
      {view.value && (
        <img
          src={view.value}
          alt=""
          className="input-image-preview"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
      <input {...common} type="url" className="input" onChange={(e) => onChange(e.target.value)} />
      {client && (
        <>
          <button
            type="button"
            className="landing-button-text input-image-browse"
            onClick={() => setGalleryOpen(true)}
            aria-label="Browse assets"
          >
            Browse
          </button>
          <AssetGallery
            client={client}
            open={galleryOpen}
            onClose={() => setGalleryOpen(false)}
            onSelect={(value) => onChange(value)}
            format={view.assetFormat}
          />
        </>
      )}
    </div>
  );
}

interface NumberProps {
  view: NumberView;
  onChange: (v: number | null) => void;
  id: string;
}

export function NumberInput({ view, onChange, id }: NumberProps): JSX.Element {
  return (
    <input
      id={id}
      type="number"
      className="input"
      value={view.value ?? ''}
      min={view.minimum}
      max={view.maximum}
      step={view.multipleOf ?? (view.integer ? 1 : 'any')}
      aria-required={view.required}
      onChange={(e) => {
        const v = e.target.value;
        if (v === '') {
          onChange(null);
          return;
        }
        const n = Number(v);
        onChange(Number.isFinite(n) ? n : null);
      }}
    />
  );
}

interface BooleanProps {
  view: BooleanView;
  onChange: (v: boolean) => void;
  id: string;
}

export function BooleanInput({ view, onChange, id }: BooleanProps): JSX.Element {
  const checked = view.value === true;
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      className={'toggle' + (checked ? ' toggle-on' : '')}
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onChange(!checked);
        }
      }}
    >
      <span className="toggle-thumb" aria-hidden="true" />
    </button>
  );
}

interface EnumProps {
  view: EnumView;
  onChange: (v: JsonValue) => void;
  id: string;
}

export function EnumInput({ view, onChange, id }: EnumProps): JSX.Element {
  const selectedIndex = view.options.findIndex((o) => deepEqual(o, view.value));
  return (
    <select
      id={id}
      className="input"
      value={selectedIndex === -1 ? '' : String(selectedIndex)}
      aria-required={view.required}
      onChange={(e) => {
        const idx = Number(e.target.value);
        const option = view.options[idx];
        if (option !== undefined) onChange(option);
      }}
    >
      {selectedIndex === -1 && <option value="">— choose —</option>}
      {view.options.map((opt, i) => (
        <option key={i} value={String(i)}>
          {formatEnumOption(opt)}
        </option>
      ))}
    </select>
  );
}

function formatEnumOption(v: JsonValue): string {
  if (v === null) return '(none)';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function deepEqual(a: JsonValue, b: JsonValue): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => deepEqual(v, b[i]!));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ak = Object.keys(a);
    const bk = Object.keys(b);
    if (ak.length !== bk.length) return false;
    return ak.every((k) =>
      deepEqual((a as Record<string, JsonValue>)[k]!, (b as Record<string, JsonValue>)[k]!),
    );
  }
  return false;
}
